import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  type ProductionPlanDay,
  type ProductionPlanItem,
  type ProductionPlanDaySummary,
} from '../api/client'
import { Toast } from '../components/ConfirmDialog'

// ──────────────────────────────────────────────────────────
// 定数 / ユーティリティ
// ──────────────────────────────────────────────────────────

const DOW_JP = ['日', '月', '火', '水', '木', '金', '土']

const WORK_TYPE_LABELS: Record<string, string> = {
  story:       'ストーリー',
  book:        '単行本作業',
  tankobon:    '単行本作業',
  color:       'カラーイラスト',
  kaikai_kiki: 'カイカイキキ',
  other:       'その他',
}

const WORKER_TYPE_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  artist:    { label: '漫画家',       bg: 'var(--blue-light)',  color: 'var(--blue)'  },
  assistant: { label: 'アシスタント', bg: 'var(--paper-3)',     color: 'var(--ink-3)' },
}

// Phase 5-B: 役割ラベル（表示用）
const ROLE_LABEL_MAP: Record<string, string> = {
  background: '背景',
  finishing:  '仕上げ',
  general:    '汎用',
  ai_art:     'AI',
  research:   '資料',
}

// Phase 5-C: 工程短縮ラベル（assistant_usage の assigned_stages 表示用）
const STAGE_LABEL_SHORT: Record<string, string> = {
  sketch:     '下書',
  background: '背景',
  finishing:  '仕上',
  ai_art:     'AI',
  research:   '資料',
  general:    '汎用',
  inking:     'ペン入',
  name:       'ネーム',
}

function getTodayJST(): string {
  const now = new Date()
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000)
  return jst.toISOString().slice(0, 10)
}

function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d + n)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

// アシスタントは平日のみ、漫画家は曜日問わず翌日を返す
function getNextWorkDay(fromDate: string, workerType: string | null): string {
  const isAssistant = workerType === 'assistant'
  for (let i = 1; i <= 7; i++) {
    const next = addDays(fromDate, i)
    const [y, m, d] = next.split('-').map(Number)
    const dow = new Date(y, m - 1, d).getDay()
    if (!isAssistant || (dow !== 0 && dow !== 6)) return next
  }
  return addDays(fromDate, 1)
}

function formatDateJP(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return `${dateStr}（${DOW_JP[date.getDay()]}）`
}

function isWeekday(dateStr: string): boolean {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dow = new Date(y, m - 1, d).getDay()
  return dow !== 0 && dow !== 6
}

function formatAssistantNames(
  assistants_by_role: Record<string, string[]> | undefined,
  fallbackNames: string[],
): string {
  if (!assistants_by_role || Object.keys(assistants_by_role).length === 0) {
    return fallbackNames.join('・')
  }
  const parts: string[] = []
  for (const [role, names] of Object.entries(assistants_by_role)) {
    const roleLabel = ROLE_LABEL_MAP[role] ?? role
    for (const name of names) {
      parts.push(`${name}（${roleLabel}）`)
    }
  }
  return parts.length > 0 ? parts.join('・') : fallbackNames.join('・')
}

const RISK_STYLE: Record<string, { bg: string; border: string; color: string; label: string }> = {
  critical: { bg: 'var(--red-light)',   border: 'var(--red)',   color: 'var(--red)',   label: '破綻見込み' },
  danger:   { bg: 'var(--red-light)',   border: 'var(--red)',   color: 'var(--red)',   label: '危険' },
  warning:  { bg: 'var(--amber-light)', border: 'var(--amber)', color: 'var(--amber)', label: '注意' },
  safe:     { bg: 'var(--green-light)', border: 'var(--green)', color: 'var(--green)', label: '安全' },
}

function riskStyle(risk: string | null) {
  return RISK_STYLE[risk ?? 'safe'] ?? RISK_STYLE.safe
}

function amountText(amount: number, unit: string): string {
  return unit === 'pages' ? `${amount}P` : `${amount}日`
}

function displayStageLabel(item: { stage_label: string | null; work_type?: string }): string {
  if (item.stage_label) {
    return WORK_TYPE_LABELS[item.stage_label] ?? item.stage_label
  }
  if (item.work_type) {
    return WORK_TYPE_LABELS[item.work_type] ?? item.work_type
  }
  return ''
}

function capPct(cost: number | undefined): string {
  if (cost == null) return ''
  return `${Math.round(cost * 100)}%`
}

// ──────────────────────────────────────────────────────────
// キャパシティバー（プレビュータブ用・フルサイズ）
// ──────────────────────────────────────────────────────────

function CapacityBar({
  label, used, capacity = 1.0, conflict,
}: { label: string; used: number; capacity?: number; conflict: string | null }) {
  if (capacity === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
        <span style={{ color: 'var(--ink-3)', minWidth: 72, flexShrink: 0 }}>{label}</span>
        <div style={{ flex: 1, height: 6, background: 'var(--paper-3)', borderRadius: 3, overflow: 'hidden', minWidth: 60 }} />
        <span style={{ fontFamily: 'var(--font-mono)', minWidth: 54, textAlign: 'right', color: 'var(--ink-3)' }}>
          0.00/0.0
        </span>
      </div>
    )
  }
  const ratio    = used / capacity
  const pct      = Math.min(100, Math.round(ratio * 100))
  const isOver   = ratio > 1.0
  const barColor = isOver ? (ratio > 1.2 ? 'var(--red)' : 'var(--amber)') : 'var(--blue)'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
      <span style={{ color: 'var(--ink-3)', minWidth: 72, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: 'var(--paper-3)', borderRadius: 3, overflow: 'hidden', minWidth: 60 }}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', minWidth: 54, textAlign: 'right', color: isOver ? barColor : 'var(--ink-2)', fontWeight: isOver ? 700 : 400 }}>
        {used.toFixed(2)}/{capacity.toFixed(1)}
      </span>
      {isOver && (
        <span style={{ color: barColor, fontSize: 10, fontWeight: 700 }}>
          {conflict === 'over_capacity' ? '⚠️超過' : '⚠超過'}
        </span>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// ミニキャパバー（圧縮リスト日別ヘッダー用）
// ──────────────────────────────────────────────────────────

function MiniCapBar({ label, used, capacity = 1.0 }: {
  label: string; used: number; capacity?: number
}) {
  const eff      = Math.max(capacity, 0.001)
  const ratio    = used / eff
  const isOver   = ratio > 1.0
  const color    = isOver ? (ratio > 1.2 ? 'var(--red)' : 'var(--amber)') : 'var(--blue)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
      <span style={{ color: 'var(--ink-3)', minWidth: 30, flexShrink: 0 }}>{label}</span>
      <div style={{ width: 56, height: 5, background: 'var(--paper-3)', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ height: '100%', width: `${Math.min(100, ratio * 100)}%`, background: color }} />
      </div>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 9,
        color: isOver ? color : 'var(--ink-3)', fontWeight: isOver ? 700 : 400,
        minWidth: 34,
      }}>
        {used.toFixed(1)}/{capacity === 0 ? '0' : capacity.toFixed(0)}
      </span>
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// Phase 5-D: 担当候補バッジ
// ──────────────────────────────────────────────────────────

const SUGGEST_STAGE_KEYS = new Set(['sketch', 'background', 'finishing'])

function SuggestedAssigneeBadge({ item }: {
  item: {
    worker_type?: string | null
    stage_key?: string | null
    suggested_assistant_name?: string | null
    suggested_assistant_reason?: string | null
    suggested_assistant_confidence?: 'high' | 'medium' | 'low' | null
  }
}) {
  if (item.worker_type !== 'assistant') return null
  if (!item.stage_key || !SUGGEST_STAGE_KEYS.has(item.stage_key)) return null
  const conf = item.suggested_assistant_confidence
  const nameStyle: React.CSSProperties = {
    fontSize: 10, padding: '1px 6px', borderRadius: 10, fontWeight: 600,
    background: conf === 'high' ? 'var(--green-light)' : conf === 'medium' ? 'var(--blue-light)' : conf === 'low' ? 'var(--amber-light)' : 'var(--paper-3)',
    color: conf === 'high' ? 'var(--green)' : conf === 'medium' ? 'var(--blue)' : conf === 'low' ? 'var(--amber)' : 'var(--ink-3)',
    border: `1px solid ${conf === 'high' ? 'var(--green)' : conf === 'medium' ? 'var(--blue)' : conf === 'low' ? 'var(--amber)' : 'var(--paper-3)'}`,
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, flexWrap: 'wrap' }}>
      <span style={{ color: 'var(--ink-3)', fontWeight: 600, fontSize: 10 }}>担当想定:</span>
      <span style={nameStyle}>{item.suggested_assistant_name ?? '未割当'}</span>
      {item.suggested_assistant_reason && (
        <span style={{ color: item.suggested_assistant_name ? 'var(--ink-3)' : 'var(--amber)', fontSize: 10 }}>
          {item.suggested_assistant_reason}
        </span>
      )}
      <span style={{ color: 'var(--ink-3)', fontSize: 9, opacity: 0.7 }}>※表示上の候補</span>
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// Phase 2: 担当者別負荷集計ヘルパー
// ──────────────────────────────────────────────────────────

type WorkerLoad = {
  key: string
  name: string
  workerType: 'artist' | 'assistant' | 'unassigned'
  totalCapCost: number
  workDays: number
  pendingCount: number
  skippedCount: number
  doneCount: number
  byDate: Record<string, number>
  topTasks: string[]
  riskCounts: { critical: number; danger: number; warning: number; safe: number }
}

function buildWorkerLoads(items: ProductionPlanItem[]): WorkerLoad[] {
  const map = new Map<string, WorkerLoad>()

  const getOrCreate = (key: string, name: string, type: WorkerLoad['workerType']): WorkerLoad => {
    if (!map.has(key)) {
      map.set(key, {
        key, name, workerType: type,
        totalCapCost: 0, workDays: 0,
        pendingCount: 0, skippedCount: 0, doneCount: 0,
        byDate: {}, topTasks: [],
        riskCounts: { critical: 0, danger: 0, warning: 0, safe: 0 },
      })
    }
    return map.get(key)!
  }

  const titleCount: Record<string, Record<string, number>> = {}

  for (const item of items) {
    const wt = item.worker_type ?? 'artist'
    let key: string, name: string, type: WorkerLoad['workerType']

    if (wt === 'assistant') {
      if (item.suggested_assistant_name) {
        key  = `assistant:${item.suggested_assistant_name}`
        name = item.suggested_assistant_name
        type = 'assistant'
      } else {
        key = 'unassigned'; name = '未割当'; type = 'unassigned'
      }
    } else {
      key = 'artist'; name = '漫画家'; type = 'artist'
    }

    const load = getOrCreate(key, name, type)

    if (item.status !== 'skipped') {
      const cc = item.capacity_cost ?? 0
      load.totalCapCost += cc
      load.byDate[item.plan_date] = (load.byDate[item.plan_date] ?? 0) + cc
    }

    if (item.status === 'skipped')    load.skippedCount++
    else if (item.status === 'done')  load.doneCount++
    else                              load.pendingCount++

    if (item.status !== 'skipped') {
      const r = item.risk
      if (r === 'critical' || r === 'danger' || r === 'warning' || r === 'safe') {
        load.riskCounts[r]++
      }
    }

    if (!titleCount[key]) titleCount[key] = {}
    const title = item.work_title ?? ''
    if (title) titleCount[key][title] = (titleCount[key][title] ?? 0) + 1
  }

  for (const [key, load] of map.entries()) {
    load.workDays = Object.keys(load.byDate).length
    load.topTasks = Object.entries(titleCount[key] ?? {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([t]) => t)
  }

  return [...map.values()].sort((a, b) => {
    const order: Record<string, number> = { artist: 0, assistant: 1, unassigned: 2 }
    if (order[a.workerType] !== order[b.workerType]) return order[a.workerType] - order[b.workerType]
    return a.name.localeCompare(b.name)
  })
}

// ──────────────────────────────────────────────────────────
// Phase 2: アラートパネル（危険/注意/未割当タスク一覧）
// ──────────────────────────────────────────────────────────

function AlertPanel({ items, onSelect }: {
  items: ProductionPlanItem[]
  onSelect: (id: string) => void
}) {
  const active     = items.filter((it) => it.status !== 'skipped' && it.status !== 'done')
  const danger     = active.filter((it) => it.risk === 'critical' || it.risk === 'danger')
  const warns      = active.filter((it) => it.risk === 'warning')
  const unassigned = active.filter(
    (it) => it.worker_type === 'assistant' &&
      SUGGEST_STAGE_KEYS.has(it.stage_key ?? '') &&
      !it.suggested_assistant_name,
  )

  if (danger.length === 0 && warns.length === 0 && unassigned.length === 0) return null

  const Section = ({
    color, bg, title, list,
  }: { color: string; bg: string; title: string; list: ProductionPlanItem[] }) => (
    <div style={{
      flex: 1, minWidth: 160,
      background: bg, border: `1px solid ${color}`,
      borderRadius: 8, padding: '8px 10px',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 5 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {list.slice(0, 4).map((it) => (
          <div
            key={it.id}
            onClick={() => onSelect(it.id)}
            title={`${it.plan_date} / ${displayStageLabel(it)}`}
            style={{
              fontSize: 10, cursor: 'pointer', padding: '2px 5px', borderRadius: 3,
              background: 'var(--paper)', color: 'var(--ink-2)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {it.plan_date.slice(5).replace('-', '/')}　{it.work_title}
            {it.episode_number != null ? ` #${it.episode_number}` : ''}
            {it.stage_key ? `（${displayStageLabel(it)}）` : ''}
          </div>
        ))}
        {list.length > 4 && (
          <div style={{ fontSize: 10, color, fontWeight: 700 }}>他 {list.length - 4} 件</div>
        )}
      </div>
    </div>
  )

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      {danger.length > 0 && (
        <Section color="var(--red)" bg="var(--red-light)"
          title={`危険/破綻 ${danger.length}件`} list={danger} />
      )}
      {warns.length > 0 && (
        <Section color="var(--amber)" bg="var(--amber-light)"
          title={`注意 ${warns.length}件`} list={warns} />
      )}
      {unassigned.length > 0 && (
        <Section color="var(--ink-3)" bg="var(--paper-2)"
          title={`未割当 ${unassigned.length}件`} list={unassigned} />
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// Phase 2: 担当者負荷マトリクスビュー
// ──────────────────────────────────────────────────────────

function LoadMatrixView({ items, onSelectTask, onViewChange }: {
  items: ProductionPlanItem[]
  onSelectTask?: (id: string) => void
  onViewChange?: (v: 'compact' | 'weekly' | 'load') => void
}) {
  const loads = buildWorkerLoads(items)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  // Phase 3: worker key → アイテム一覧のマッピング
  const itemsByKey = items.reduce<Record<string, ProductionPlanItem[]>>((acc, item) => {
    const wt = item.worker_type ?? 'artist'
    let key: string
    if (wt === 'assistant') {
      key = item.suggested_assistant_name ? `assistant:${item.suggested_assistant_name}` : 'unassigned'
    } else {
      key = 'artist'
    }
    ;(acc[key] ??= []).push(item)
    return acc
  }, {})

  if (items.length === 0) {
    return (
      <div className="card">
        <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 13, textAlign: 'center', padding: 24 }}>
          保存済み計画がありません
        </div>
      </div>
    )
  }

  const TABLE_HEADERS = ['', '担当', '種別', '合計負荷', '平均/日', '過密日', '危険/注意', '未完了', '主なタスク']

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* 表形式マトリクス */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 6 }}>
          <span className="card-title">担当者負荷マトリクス</span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            ※ アシスタント欄は候補（表示上の推定）です
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--paper-2)', borderBottom: '2px solid var(--paper-3)' }}>
                {TABLE_HEADERS.map((h, i) => (
                  <th key={i} style={{
                    padding: '7px 10px',
                    textAlign: (h === '担当' || h === '主なタスク' || h === '') ? 'left' : 'right',
                    fontWeight: 600, fontSize: 11, color: 'var(--ink-2)', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loads.map((load, idx) => {
                const avgPerDay  = load.workDays > 0 ? load.totalCapCost / load.workDays : 0
                const loadPct    = Math.round(avgPerDay * 100)
                const denseDays  = Object.values(load.byDate).filter((v) => v > 1.0).length
                const color      = load.workerType === 'unassigned' ? 'var(--ink-3)'
                  : loadPct > 100 ? 'var(--red)'
                  : loadPct >= 80 ? 'var(--amber)'
                  : 'var(--green)'
                const bg         = load.workerType === 'unassigned' ? 'var(--paper-2)'
                  : loadPct > 100 ? 'var(--red-light)'
                  : loadPct >= 80 ? 'var(--amber-light)'
                  : 'var(--green-light)'
                const isExpanded = expandedKey === load.key
                const rowItems   = itemsByKey[load.key] ?? []

                return (
                  <Fragment key={load.key}>
                    {/* データ行 */}
                    <tr style={{
                      background: idx % 2 === 0 ? 'var(--paper)' : 'var(--paper-2)',
                      borderBottom: isExpanded ? 'none' : '1px solid var(--paper-3)',
                    }}>
                      {/* 展開トグル */}
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 10, padding: '1px 5px', color: 'var(--ink-3)' }}
                          onClick={() => setExpandedKey(isExpanded ? null : load.key)}
                          title={isExpanded ? 'タスク一覧を閉じる' : 'タスク一覧を開く'}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                      <td style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{load.name}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 3,
                          background: load.workerType === 'artist' ? 'var(--blue-light)'
                            : load.workerType === 'assistant' ? 'var(--paper-3)'
                            : 'var(--amber-light)',
                          color: load.workerType === 'artist' ? 'var(--blue)'
                            : load.workerType === 'assistant' ? 'var(--ink-3)'
                            : 'var(--amber)',
                        }}>
                          {load.workerType === 'artist' ? '漫画家' : load.workerType === 'assistant' ? 'アシ' : '未割当'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {load.totalCapCost.toFixed(1)}日
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                        <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: bg, color, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                          {load.workerType === 'unassigned' ? '—' : `${loadPct}%`}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11, color: denseDays > 0 ? 'var(--red)' : 'var(--ink-3)' }}>
                        {denseDays > 0 ? `${denseDays}日` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11 }}>
                        {load.riskCounts.critical > 0 && (
                          <span style={{ color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>破綻{load.riskCounts.critical} </span>
                        )}
                        {load.riskCounts.danger > 0 && (
                          <span style={{ color: 'var(--amber)', fontFamily: 'var(--font-mono)' }}>危険{load.riskCounts.danger}</span>
                        )}
                        {load.riskCounts.critical === 0 && load.riskCounts.danger === 0 && (
                          <span style={{ color: 'var(--ink-3)' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {load.pendingCount}件
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--ink-3)', fontSize: 11, maxWidth: 220 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {load.topTasks.join(' / ') || '—'}
                        </div>
                      </td>
                    </tr>

                    {/* 展開行: タスク一覧 */}
                    {isExpanded && (
                      <tr style={{ borderBottom: '2px solid var(--paper-3)' }}>
                        <td colSpan={TABLE_HEADERS.length} style={{ padding: '6px 12px 10px 28px', background: idx % 2 === 0 ? 'var(--paper)' : 'var(--paper-2)' }}>
                          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 5, fontWeight: 600 }}>
                            タスク一覧（{rowItems.length}件）— クリックで圧縮リストに移動
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 240, overflowY: 'auto' }}>
                            {rowItems
                              .slice()
                              .sort((a, b) => a.plan_date.localeCompare(b.plan_date))
                              .map((it) => {
                                const s = riskStyle(it.status === 'done' ? null : it.risk)
                                return (
                                  <div
                                    key={it.id}
                                    onClick={() => {
                                      onSelectTask?.(it.id)
                                      onViewChange?.('compact')
                                    }}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 6,
                                      padding: '3px 8px', borderRadius: 4,
                                      cursor: 'pointer', userSelect: 'none',
                                      borderLeft: `2px solid ${it.status === 'done' ? 'var(--paper-3)' : s.border}`,
                                      background: 'var(--paper)',
                                      opacity: it.status === 'skipped' ? 0.55 : 1,
                                    }}
                                    title={`${it.plan_date} / ${displayStageLabel(it)}`}
                                  >
                                    <span style={{ fontSize: 9, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                                      {it.plan_date.slice(5).replace('-', '/')}
                                    </span>
                                    <span style={{
                                      flex: 1, fontSize: 10, fontWeight: 600,
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      color: it.status === 'done' ? 'var(--ink-3)' : 'var(--ink)',
                                      textDecoration: it.status === 'done' ? 'line-through' : undefined,
                                    }}>
                                      {it.work_title}{it.episode_number != null ? ` #${it.episode_number}` : ''}
                                      {it.stage_key ? ` / ${displayStageLabel(it)}` : ''}
                                    </span>
                                    <span style={{ fontSize: 9, padding: '0 4px', borderRadius: 3, background: s.bg, color: s.color, flexShrink: 0 }}>
                                      {it.status === 'done' ? '完了' : it.status === 'skipped' ? 'スキップ' : s.label}
                                    </span>
                                  </div>
                                )
                              })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 日別スパークライン */}
      {loads.some((l) => l.workDays > 0) && (
        <div className="card">
          <div className="card-header"><span className="card-title" style={{ fontSize: 13 }}>日別負荷スパーク</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loads.filter((l) => l.workDays > 0).map((load) => {
              const dates  = Object.keys(load.byDate).sort()
              const maxVal = Math.max(...Object.values(load.byDate), 1.0)
              return (
                <div key={load.key} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-2)', minWidth: 80, flexShrink: 0, paddingBottom: 2 }}>
                    {load.name}
                  </span>
                  <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 24, flex: 1, minWidth: 0 }}>
                    {dates.map((date) => {
                      const val   = load.byDate[date] ?? 0
                      const ratio = val / maxVal
                      const barColor = val > 1.0 ? 'var(--red)' : val >= 0.8 ? 'var(--amber)' : 'var(--blue)'
                      return (
                        <div
                          key={date}
                          title={`${date}: ${val.toFixed(2)}`}
                          style={{
                            flex: 1, height: `${Math.max(4, Math.round(ratio * 24))}px`,
                            background: barColor, borderRadius: 2, minWidth: 3, maxWidth: 20,
                          }}
                        />
                      )
                    })}
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', minWidth: 44, textAlign: 'right', paddingBottom: 2 }}>
                    {load.workerType !== 'unassigned'
                      ? `${Math.round(load.totalCapCost / Math.max(load.workDays, 1) * 100)}%avg`
                      : `${load.totalCapCost.toFixed(1)}日`}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// Phase 2: 週次ボードビュー
// ──────────────────────────────────────────────────────────

function WeeklyBoardView({ items, daySummaries, startDate, selectedItemId, onSelect }: {
  items: ProductionPlanItem[]
  daySummaries: ProductionPlanDaySummary[]
  startDate: string
  selectedItemId: string | null
  onSelect: (id: string | null) => void
}) {
  const [weekOffset, setWeekOffset] = useState(0)

  const today = getTodayJST()

  // 指定日付の月曜日を返す
  const getMondayOf = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    const dow  = date.getDay()
    const diff = dow === 0 ? -6 : 1 - dow
    const mon  = new Date(date.getTime() + diff * 86400 * 1000)
    return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, '0')}-${String(mon.getDate()).padStart(2, '0')}`
  }

  // 2つの月曜日間の週数差を返す
  const weeksBetween = (from: string, to: string): number => {
    const [fy, fm, fd] = from.split('-').map(Number)
    const [ty, tm, td] = to.split('-').map(Number)
    const f = new Date(fy, fm - 1, fd)
    const t = new Date(ty, tm - 1, td)
    return Math.round((t.getTime() - f.getTime()) / (7 * 86400 * 1000))
  }

  const baseMon    = getMondayOf(startDate)
  const weekStart  = addDays(baseMon, weekOffset * 7)
  const weekDates  = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const weekEnd    = weekDates[6]

  const itemsByDate = items.reduce<Record<string, ProductionPlanItem[]>>((acc, it) => {
    ;(acc[it.plan_date] ??= []).push(it)
    return acc
  }, {})

  const summaryByDate = new Map<string, ProductionPlanDaySummary>()
  for (const s of daySummaries) summaryByDate.set(s.date, s)

  const weekItemCount = weekDates.reduce((s, d) => s + (itemsByDate[d]?.length ?? 0), 0)
  const todayMon      = getMondayOf(today)
  const todayOffset   = weeksBetween(baseMon, todayMon)
  const isAtToday     = weekOffset === todayOffset

  return (
    <div>
      {/* ナビゲーション */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-sm btn-outline" onClick={() => setWeekOffset((w) => w - 1)}>← 前週</button>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
          {weekStart} 〜 {weekEnd}
        </span>
        <button className="btn btn-sm btn-outline" onClick={() => setWeekOffset((w) => w + 1)}>翌週 →</button>
        {!isAtToday && (
          <button
            className="btn btn-sm btn-outline"
            onClick={() => setWeekOffset(todayOffset)}
            style={{ color: 'var(--blue)', borderColor: 'var(--blue)' }}
          >
            今週へ
          </button>
        )}
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          {weekItemCount > 0 ? `この週 ${weekItemCount}件` : 'この週に計画なし'}
        </span>
      </div>

      {/* 週グリッド */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(110px, 1fr))',
          gap: 5,
          minWidth: 700,
        }}>
          {weekDates.map((date) => {
            const [y, m, d] = date.split('-').map(Number)
            const dow       = new Date(y, m - 1, d).getDay()
            const isWeekend = dow === 0 || dow === 6
            const isToday   = date === today
            const summary   = summaryByDate.get(date)
            const cl        = summary?.conflict_level ?? null
            const dayItems  = itemsByDate[date] ?? []
            const doneCount = dayItems.filter((it) => it.status === 'done').length

            return (
              <div key={date} style={{
                borderRadius: 8,
                border: isToday
                  ? '2px solid var(--blue)'
                  : cl === 'over_capacity' ? '1px solid var(--red)'
                  : cl === 'warning'       ? '1px solid var(--amber)'
                  : '1px solid var(--paper-3)',
                background: isWeekend ? 'var(--paper-2)' : 'var(--paper)',
                overflow: 'hidden',
              }}>
                {/* 日ヘッダー */}
                <div style={{
                  padding: '5px 7px',
                  borderBottom: '1px solid var(--paper-3)',
                  background: isToday ? 'var(--blue-light)'
                    : cl === 'over_capacity' ? 'var(--red-light)'
                    : cl === 'warning'       ? 'var(--amber-light)'
                    : 'var(--paper-2)',
                }}>
                  <div style={{
                    fontSize: 12, fontWeight: 700,
                    color: isToday ? 'var(--blue)' : isWeekend ? 'var(--ink-3)' : 'var(--ink)',
                    display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
                  }}>
                    {DOW_JP[dow]} {m}/{d}
                    {isToday && (
                      <span style={{ fontSize: 9, background: 'var(--blue)', color: 'var(--paper)', borderRadius: 3, padding: '0 3px' }}>今日</span>
                    )}
                    {cl && (
                      <span style={{
                        fontSize: 9,
                        background: cl === 'over_capacity' ? 'var(--red)' : 'var(--amber)',
                        color: 'var(--paper)', borderRadius: 3, padding: '0 3px',
                      }}>
                        {cl === 'over_capacity' ? '超過' : '注意'}
                      </span>
                    )}
                  </div>
                  {dayItems.length > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1 }}>
                      {doneCount}/{dayItems.length}完了
                    </div>
                  )}
                </div>

                {/* タスクカード */}
                <div style={{ padding: '4px', display: 'flex', flexDirection: 'column', gap: 2, minHeight: 48 }}>
                  {dayItems.length === 0 ? (
                    <div style={{ fontSize: 10, color: 'var(--ink-3)', textAlign: 'center', paddingTop: 10 }}>
                      {isWeekend ? '休日' : '—'}
                    </div>
                  ) : dayItems.map((item) => {
                    const isDone    = item.status === 'done'
                    const isSkipped = item.status === 'skipped'
                    const isSelected = item.id === selectedItemId
                    const s = riskStyle(isDone ? null : item.risk)
                    const workerIcon = item.worker_type === 'artist' ? '🎨' : item.worker_type === 'assistant' ? '👤' : ''
                    return (
                      <div
                        key={item.id}
                        onClick={() => onSelect(isSelected ? null : item.id)}
                        style={{
                          padding: '3px 5px', borderRadius: 4,
                          cursor: 'pointer', userSelect: 'none',
                          borderLeft: `2px solid ${isSelected ? 'var(--blue)' : isDone || isSkipped ? 'var(--paper-3)' : s.border}`,
                          background: isSelected ? 'var(--blue-light)' : isDone || isSkipped ? 'var(--paper-2)' : s.bg,
                          opacity: isSkipped ? 0.55 : 1,
                          outline: isSelected ? '1px solid var(--blue)' : undefined,
                          outlineOffset: '-1px',
                        }}
                        title={`${item.work_title}${item.episode_number != null ? ` #${item.episode_number}` : ''} / ${displayStageLabel(item)}`}
                      >
                        <div style={{
                          fontSize: 10, fontWeight: 600, lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          color: isDone ? 'var(--ink-3)' : 'var(--ink)',
                          textDecoration: isDone ? 'line-through' : undefined,
                        }}>
                          {workerIcon} {item.work_title}{item.episode_number != null ? ` #${item.episode_number}` : ''}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                          {amountText(item.planned_amount, item.unit)}
                          {item.is_locked === 1 && ' 🔒'}
                          {isDone && ' ✓'}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// プレビュー用アイテム行（変更なし）
// ──────────────────────────────────────────────────────────

function PreviewItemRow({ item }: { item: ProductionPlanDay['items'][number] }) {
  const s           = riskStyle(item.risk)
  const stageLabel  = displayStageLabel(item)
  const workerType  = item.worker_type ?? null
  const workerBadge = workerType ? (WORKER_TYPE_BADGE[workerType] ?? null) : null
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 12px',
      background: s.bg, borderLeft: `3px solid ${s.border}`, borderRadius: 6,
    }}>
      <div style={{ minWidth: 68, paddingTop: 1 }}>
        <span className="badge" style={{
          background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 10, whiteSpace: 'nowrap',
        }}>
          {s.label}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
          {item.work_title}
          {item.episode_number != null ? ` #${item.episode_number}` : ''}
          {stageLabel ? ` / ${stageLabel}` : ''}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>{amountText(item.planned_amount, item.unit)}</span>
          {workerBadge && (
            <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: workerBadge.bg, color: workerBadge.color }}>
              {workerBadge.label}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{item.reason}</div>
        {/* Phase 5-D: 担当候補 */}
        <SuggestedAssigneeBadge item={item} />
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// KPI カード行（保存済みタブ用）
// ──────────────────────────────────────────────────────────

function KpiCards({ items, daySummaries }: {
  items: ProductionPlanItem[]
  daySummaries: ProductionPlanDaySummary[]
}) {
  const activeItems  = items.filter((it) => it.status !== 'skipped')
  const doneCount    = activeItems.filter((it) => it.status === 'done').length
  const totalCount   = activeItems.length

  const dangerDays = new Set(
    daySummaries
      .filter((s) => s.conflict_level === 'over_capacity' || s.conflict_level === 'critical')
      .map((s) => s.date)
  ).size

  const unassigned = items.filter((it) =>
    it.worker_type === 'assistant' &&
    SUGGEST_STAGE_KEYS.has(it.stage_key ?? '') &&
    !it.suggested_assistant_id &&
    it.status !== 'skipped'
  ).length

  const artistItems = activeItems.filter((it) => (it.worker_type ?? 'artist') === 'artist')
  const assistItems = activeItems.filter((it) => it.worker_type === 'assistant')
  const artistTotal = artistItems.reduce((s, it) => s + it.planned_amount, 0)
  const assistTotal = assistItems.reduce((s, it) => s + it.planned_amount, 0)
  const artistUnit  = artistItems[0]?.unit ?? 'pages'
  const assistUnit  = assistItems[0]?.unit ?? 'pages'

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))',
      gap: 8,
      marginBottom: 12,
    }}>
      {[
        { label: '保存済み計画',   value: `${totalCount}件`,                 sub: `完了${doneCount}件`,          color: undefined },
        { label: '漫画家作業',     value: amountText(artistTotal, artistUnit), sub: '予定作業量',                   color: undefined },
        { label: 'アシスタント',   value: amountText(assistTotal, assistUnit), sub: '予定作業量',                   color: undefined },
        { label: '危険日',         value: `${dangerDays}日`,                 sub: 'キャパ超過',                   color: dangerDays   > 0 ? 'var(--red)'   : undefined },
        { label: '未割当',         value: `${unassigned}件`,                 sub: 'アシ担当候補なし',              color: unassigned   > 0 ? 'var(--amber)' : undefined },
      ].map((k) => (
        <div key={k.label} className="metric">
          <div className="metric-label">{k.label}</div>
          <div className="metric-value" style={{ color: k.color, fontSize: 18 }}>{k.value}</div>
          <div className="metric-sub">{k.sub}</div>
        </div>
      ))}
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// 圧縮タスクカード（クリックで右パネル表示）
// ──────────────────────────────────────────────────────────

function CompactTaskCard({ item, isSelected, onClick }: {
  item: ProductionPlanItem
  isSelected: boolean
  onClick: () => void
}) {
  const isDone     = item.status === 'done'
  const isSkipped  = item.status === 'skipped'
  const s          = riskStyle(isDone ? null : item.risk)
  const stageLabel = displayStageLabel(item)
  const workerBadge = WORKER_TYPE_BADGE[item.worker_type ?? '']

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 10px',
        borderRadius: 6,
        cursor: 'pointer',
        borderLeft: `3px solid ${isSelected ? 'var(--blue)' : isDone || isSkipped ? 'var(--paper-3)' : s.border}`,
        background: isSelected
          ? 'var(--blue-light)'
          : isDone || isSkipped ? 'var(--paper-2)' : s.bg,
        opacity: isSkipped ? 0.6 : 1,
        outline: isSelected ? '2px solid var(--blue)' : undefined,
        outlineOffset: isSelected ? '-1px' : undefined,
        transition: 'background 0.1s',
        userSelect: 'none',
      }}
    >
      {/* リスク丸ドット */}
      <span style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        background: isDone ? 'var(--green)' : isSkipped ? 'var(--paper-3)' : s.border,
      }} />

      {/* メイン情報 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          textDecoration: isDone ? 'line-through' : undefined,
          color: isDone ? 'var(--ink-3)' : undefined,
        }}>
          {item.work_title}
          {item.episode_number != null ? ` #${item.episode_number}` : ''}
          {stageLabel ? ` / ${stageLabel}` : ''}
        </div>
        <div style={{ fontSize: 10, color: 'var(--ink-3)', display: 'flex', gap: 4, alignItems: 'center', marginTop: 1, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{amountText(item.planned_amount, item.unit)}</span>
          {workerBadge && (
            <span style={{ background: workerBadge.bg, color: workerBadge.color, padding: '0 4px', borderRadius: 3, fontSize: 9 }}>
              {workerBadge.label}
            </span>
          )}
          {item.is_locked === 1 && <span style={{ color: 'var(--blue)', fontSize: 9 }}>🔒</span>}
          {item.is_manual_moved === 1 && item.moved_from_date && (
            <span style={{ color: 'var(--amber)', fontSize: 9 }}>↑移動</span>
          )}
          {/* Phase 5-D: 担当候補名（コンパクト） */}
          {item.worker_type === 'assistant' && item.stage_key && SUGGEST_STAGE_KEYS.has(item.stage_key) && (
            <span style={{
              fontSize: 9, padding: '0 4px', borderRadius: 3,
              background: item.suggested_assistant_name ? 'var(--green-light)' : 'var(--paper-3)',
              color:      item.suggested_assistant_name ? 'var(--green)'       : 'var(--ink-3)',
            }}>
              {item.suggested_assistant_name ?? '未割当'}
            </span>
          )}
        </div>
      </div>

      {/* ステータス / リスクバッジ */}
      {isSkipped ? (
        <span style={{ fontSize: 9, color: 'var(--ink-3)', flexShrink: 0 }}>スキップ</span>
      ) : isDone ? (
        <span style={{ fontSize: 9, color: 'var(--green)', flexShrink: 0 }}>✓完了</span>
      ) : (
        <span style={{
          fontSize: 9, padding: '1px 5px', borderRadius: 4, flexShrink: 0,
          background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        }}>
          {s.label}
        </span>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// Phase 3: 解消候補ヒント計算（純関数・副作用なし）
// ──────────────────────────────────────────────────────────

function computeResolutionHints(
  item: ProductionPlanItem,
  daySummary?: ProductionPlanDaySummary | null,
): string[] {
  if (item.status === 'done' || item.status === 'skipped') return []
  const hints: string[] = []
  const isAssistant = item.worker_type === 'assistant'

  if (daySummary?.conflict_level === 'over_capacity') {
    hints.push('この日はキャパシティが超過しています。翌日移動または延期を検討してください。')
  } else if (daySummary?.conflict_level === 'warning') {
    hints.push('この日は負荷注意状態です。余裕のある日への移動を検討してください。')
  }
  if (item.risk === 'critical') {
    hints.push('締切が迫っているか超過しています。優先対応または固定で保護してください。')
  }
  if (isAssistant && SUGGEST_STAGE_KEYS.has(item.stage_key ?? '') && !item.suggested_assistant_name) {
    hints.push('担当候補がありません。アシスタント確定シフトが設定されているか確認してください。')
  }
  if (item.is_locked === 1 && (item.risk === 'critical' || item.risk === 'danger' || item.risk === 'warning')) {
    hints.push('固定されているため危険状態でも再生成されません。固定解除して再配置を検討してください。')
  }
  if (isAssistant && daySummary && (daySummary.assistant_capacity ?? 0) > 0) {
    const remaining = (daySummary.assistant_capacity ?? 0) - (daySummary.assistant_used ?? 0)
    const cost = item.capacity_cost ?? 0
    if (remaining < cost && remaining >= 0) {
      hints.push(`アシスタントキャパ残り ${remaining.toFixed(1)} 日分に対し ${cost.toFixed(1)} 日分必要です。`)
    }
  }
  return hints
}

// ──────────────────────────────────────────────────────────
// 右側詳細パネル（選択アイテムの全操作）
// ──────────────────────────────────────────────────────────

function ItemDetailPanel({ item, daySummary, onUpdated, showToast, onClose }: {
  item: ProductionPlanItem
  daySummary?: ProductionPlanDaySummary | null
  onUpdated: (updated: ProductionPlanItem) => void
  showToast: (ok: boolean, msg: string) => void
  onClose?: () => void
}) {
  const [editAmount, setEditAmount] = useState(false)
  const [amountVal,  setAmountVal]  = useState(String(item.planned_amount))
  const [editNote,   setEditNote]   = useState(false)
  const [noteVal,    setNoteVal]    = useState(item.manual_note ?? '')
  const [editDate,   setEditDate]   = useState(false)
  const [dateVal,    setDateVal]    = useState(item.plan_date)
  const [acting,     setActing]     = useState(false)

  // item が更新されたら（同一 item, 別の値）ローカル state を同期
  useEffect(() => { if (!editAmount) setAmountVal(String(item.planned_amount)) }, [item.planned_amount]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!editNote)   setNoteVal(item.manual_note ?? '')        }, [item.manual_note])    // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!editDate)   setDateVal(item.plan_date)                }, [item.plan_date])      // eslint-disable-line react-hooks/exhaustive-deps

  const s          = riskStyle(item.risk)
  const stageLabel = displayStageLabel(item)
  const workerBadge = WORKER_TYPE_BADGE[item.worker_type ?? '']
  const isDone     = item.status === 'done'
  const isSkipped  = item.status === 'skipped'

  const act = async (fn: () => Promise<void>) => {
    if (acting) return
    setActing(true)
    try { await fn() } finally { setActing(false) }
  }

  const saveAmount = async () => {
    const n = parseFloat(amountVal)
    if (isNaN(n) || n < 0) { setAmountVal(String(item.planned_amount)); return }
    const res = await api.productionPlanning.updatePlanItem(item.id, { planned_amount: n })
    if (res.item) onUpdated(res.item)
    setEditAmount(false)
  }

  const saveNote = async () => {
    const res = await api.productionPlanning.updatePlanItem(item.id, { manual_note: noteVal || null })
    if (res.item) onUpdated(res.item)
    setEditNote(false)
  }

  const saveDate = () => {
    if (!dateVal || dateVal === item.plan_date) { setEditDate(false); return }
    act(async () => {
      const res = await api.productionPlanning.movePlanItem(item.id, { plan_date: dateVal, reason: 'manual_move' })
      if (res.item) {
        onUpdated(res.item)
        showToast(true, `${item.plan_date} → ${dateVal} に移動しました`)
      }
      setEditDate(false)
    })
  }

  const handleLock = () => act(async () => {
    if (item.is_locked) {
      await api.productionPlanning.unlockPlanItem(item.id)
      onUpdated({ ...item, is_locked: 0 })
    } else {
      await api.productionPlanning.lockPlanItem(item.id)
      onUpdated({ ...item, is_locked: 1 })
    }
  })

  const handleSkip = () => act(async () => {
    await api.productionPlanning.skipPlanItem(item.id)
    onUpdated({ ...item, status: 'skipped' })
  })

  const handleUnSkip = () => act(async () => {
    const res = await api.productionPlanning.updatePlanItem(item.id, { status: 'planned' })
    if (res.item) onUpdated(res.item)
  })

  const handleDefer = () => act(async () => {
    try {
      const res = await api.productionPlanning.deferPlanItem(item.id)
      if (res.item) {
        onUpdated(res.item)
        showToast(true, `${res.from_date} → ${res.to_date} に延期しました`)
      }
    } catch (err) {
      showToast(false, (err as Error).message ?? '延期に失敗しました')
    }
  })

  const handleStatusToggle = () => act(async () => {
    const next = item.status === 'done' ? 'planned' : 'done'
    const res = await api.productionPlanning.updatePlanItem(item.id, { status: next })
    if (res.item) onUpdated(res.item)
  })

  // Phase 3: クイック日付移動
  const moveToDate = (targetDate: string, label: string) => {
    if (acting || targetDate === item.plan_date) return
    act(async () => {
      const res = await api.productionPlanning.movePlanItem(item.id, { plan_date: targetDate, reason: 'manual_move' })
      if (res.item) {
        onUpdated(res.item)
        showToast(true, `${label}（${targetDate}）に移動しました`)
      }
    })
  }

  return (
    <div className="card" style={{ overflow: 'visible' }}>
      {/* パネルヘッダー */}
      <div className="card-header" style={{
        background: isDone || isSkipped ? 'var(--paper-2)' : s.bg,
        flexWrap: 'wrap', gap: 6,
        alignItems: 'flex-start',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* バッジ行 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
            <span className="badge" style={{
              background: isDone ? 'var(--paper-3)' : isSkipped ? 'var(--paper-3)' : s.bg,
              color:      isDone ? 'var(--ink-3)'  : isSkipped ? 'var(--ink-3)'  : s.color,
              border:     `1px solid ${isDone || isSkipped ? 'var(--paper-3)' : s.border}`,
              fontSize: 10,
            }}>
              {isSkipped ? 'スキップ' : isDone ? '✓完了' : s.label}
            </span>
            {item.is_locked === 1 && (
              <span style={{ fontSize: 10, color: 'var(--blue)' }}>🔒固定</span>
            )}
            {item.is_manual_moved === 1 && item.moved_from_date && (
              <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'var(--amber-light)', color: 'var(--amber)' }}>
                移動({item.moved_from_date.slice(5).replace('-', '/')})
              </span>
            )}
          </div>
          {/* タイトル */}
          <div style={{
            fontWeight: 700, fontSize: 13, lineHeight: 1.4,
            textDecoration: isDone ? 'line-through' : undefined,
            color: isDone ? 'var(--ink-3)' : undefined,
          }}>
            {item.work_title}
            {item.episode_number != null ? ` #${item.episode_number}` : ''}
            {stageLabel ? ` / ${stageLabel}` : ''}
          </div>
        </div>
        {onClose && (
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '2px 6px', flexShrink: 0 }}>✕</button>
        )}
      </div>

      {/* パネル本体 */}
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* 情報グリッド */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '6px 10px' }}>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>日付</div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{formatDateJP(item.plan_date)}</div>
          </div>
          <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '6px 10px' }}>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>担当種別</div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              {workerBadge ? (
                <span style={{ background: workerBadge.bg, color: workerBadge.color, padding: '1px 6px', borderRadius: 3 }}>
                  {workerBadge.label}
                </span>
              ) : '—'}
            </div>
          </div>
        </div>

        {/* 予定量（インライン編集） */}
        <div>
          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>予定量</div>
          {editAmount ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="number" min="0" step="1"
                value={amountVal}
                onChange={(e) => setAmountVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveAmount()
                  if (e.key === 'Escape') { setEditAmount(false); setAmountVal(String(item.planned_amount)) }
                }}
                autoFocus
                style={{ width: 80, fontSize: 13 }}
              />
              <span style={{ fontSize: 12 }}>{item.unit === 'pages' ? 'P' : '日'}</span>
              <button className="btn btn-primary btn-sm" onClick={saveAmount} disabled={acting}>保存</button>
              <button className="btn btn-outline btn-sm" onClick={() => { setEditAmount(false); setAmountVal(String(item.planned_amount)) }}>取消</button>
            </div>
          ) : (
            <div
              style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="クリックで編集"
              onClick={() => setEditAmount(true)}
            >
              {amountText(item.planned_amount, item.unit)}
              {item.capacity_cost != null && item.capacity_cost > 0 && (
                <span style={{ fontSize: 10, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                  cap:{capPct(item.capacity_cost)}
                </span>
              )}
              <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>✎</span>
            </div>
          )}
        </div>

        {/* リスク理由 */}
        {item.reason && (
          <div style={{
            fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.5,
            background: 'var(--paper-2)', borderRadius: 6, padding: '6px 10px',
          }}>
            {item.reason}
          </div>
        )}

        {/* Phase 3: 解消候補ヒント */}
        {computeResolutionHints(item, daySummary).length > 0 && (
          <div style={{
            background: 'var(--amber-light)', border: '1px solid var(--amber)',
            borderRadius: 6, padding: '7px 10px',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber)', marginBottom: 4 }}>
              ⚡ 解消候補
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {computeResolutionHints(item, daySummary).map((hint, i) => (
                <div key={i} style={{ fontSize: 10, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                  · {hint}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phase 3: 担当候補 + メモに記録 */}
        {item.worker_type === 'assistant' && item.stage_key && SUGGEST_STAGE_KEYS.has(item.stage_key) && (
          <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '7px 10px' }}>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 600, marginBottom: 5 }}>担当候補</div>
            {item.suggested_assistant_name ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 10,
                  background: item.suggested_assistant_confidence === 'high'   ? 'var(--green-light)'
                    : item.suggested_assistant_confidence === 'medium' ? 'var(--blue-light)'
                    : 'var(--amber-light)',
                  color: item.suggested_assistant_confidence === 'high'   ? 'var(--green)'
                    : item.suggested_assistant_confidence === 'medium' ? 'var(--blue)'
                    : 'var(--amber)',
                  border: `1px solid ${item.suggested_assistant_confidence === 'high'   ? 'var(--green)'
                    : item.suggested_assistant_confidence === 'medium' ? 'var(--blue)'
                    : 'var(--amber)'}`,
                }}>
                  {item.suggested_assistant_name}
                </span>
                {item.suggested_assistant_reason && (
                  <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{item.suggested_assistant_reason}</span>
                )}
                <button
                  className="btn btn-sm btn-outline"
                  style={{ fontSize: 10, padding: '1px 7px', color: 'var(--blue)' }}
                  disabled={acting}
                  title="メモに「[担当: 名前]」を追記します（DB への担当者保存ではありません）"
                  onClick={() => {
                    const tag = `[担当: ${item.suggested_assistant_name}]`
                    const current = item.manual_note ?? ''
                    if (current.includes(tag)) {
                      showToast(false, 'すでにメモに記録済みです')
                      return
                    }
                    const next = current ? `${current} ${tag}` : tag
                    act(async () => {
                      const res = await api.productionPlanning.updatePlanItem(item.id, { manual_note: next })
                      if (res.item) {
                        onUpdated(res.item)
                        showToast(true, `${item.suggested_assistant_name} をメモに記録しました`)
                      }
                    })
                  }}
                >
                  メモに記録
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                担当候補なし — 確定シフトを確認してください
              </div>
            )}
          </div>
        )}

        {/* メモ（インライン編集） */}
        <div>
          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>メモ</div>
          {editNote ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input
                type="text"
                value={noteVal}
                onChange={(e) => setNoteVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveNote(); if (e.key === 'Escape') setEditNote(false) }}
                placeholder="メモを入力..."
                autoFocus
                style={{ fontSize: 12 }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-primary btn-sm" onClick={saveNote} disabled={acting}>保存</button>
                <button className="btn btn-outline btn-sm" onClick={() => setEditNote(false)}>取消</button>
              </div>
            </div>
          ) : (
            <div
              style={{
                fontSize: 11, cursor: 'pointer', padding: '6px 10px', borderRadius: 6,
                background: 'var(--paper-2)',
                color: item.manual_note ? 'var(--ink-2)' : 'var(--ink-3)',
              }}
              onClick={() => setEditNote(true)}
              title="クリックで編集"
            >
              📝 {item.manual_note || 'メモを追加...（クリックで編集）'}
            </div>
          )}
        </div>

        {/* 日付移動フォーム（展開時のみ） */}
        {editDate && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>移動先日付</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                value={dateVal}
                onChange={(e) => setDateVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveDate()
                  if (e.key === 'Escape') setEditDate(false)
                }}
                autoFocus
                style={{ fontSize: 12 }}
              />
              <button className="btn btn-primary btn-sm" onClick={saveDate} disabled={acting}>移動</button>
              <button className="btn btn-outline btn-sm" onClick={() => { setEditDate(false); setDateVal(item.plan_date) }}>取消</button>
            </div>
          </div>
        )}

        {/* Phase 3: クイック日付移動 */}
        {!isDone && !isSkipped && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>クイック移動</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <button
                className="btn btn-sm btn-outline"
                style={{ fontSize: 10, padding: '2px 8px' }}
                disabled={acting}
                title={`${addDays(item.plan_date, -1)} に移動`}
                onClick={() => moveToDate(addDays(item.plan_date, -1), '前日')}
              >
                ← 前日
              </button>
              <button
                className="btn btn-sm btn-outline"
                style={{ fontSize: 10, padding: '2px 8px' }}
                disabled={acting}
                title={`${addDays(item.plan_date, 1)} に移動`}
                onClick={() => moveToDate(addDays(item.plan_date, 1), '翌日')}
              >
                翌日 →
              </button>
              <button
                className="btn btn-sm btn-outline"
                style={{ fontSize: 10, padding: '2px 8px', color: 'var(--blue)' }}
                disabled={acting}
                title={`${getNextWorkDay(item.plan_date, item.worker_type ?? null)} に移動`}
                onClick={() => moveToDate(getNextWorkDay(item.plan_date, item.worker_type ?? null), '次の作業日')}
              >
                次の作業日 →
              </button>
            </div>
          </div>
        )}

        {/* 操作ボタン群 */}
        <div style={{ borderTop: '1px solid var(--paper-3)', paddingTop: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 6 }}>操作</div>
          {isSkipped ? (
            <button className="btn btn-outline btn-sm" onClick={handleUnSkip} disabled={acting}>
              スキップを戻す
            </button>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {/* 完了 */}
              <button
                className={`btn btn-sm ${isDone ? 'btn-primary' : 'btn-outline'}`}
                onClick={handleStatusToggle}
                disabled={acting}
                title={isDone ? '計画済みに戻します' : '予定として消化済みにします（実進捗ページ数は増えません。実績は工程更新または日報反映で）'}
              >
                {isDone ? '✓完了済み' : '完了'}
              </button>

              {/* 延期 */}
              {!isDone && (
                <button
                  className="btn btn-sm btn-outline"
                  onClick={handleDefer}
                  disabled={acting}
                  style={{ color: 'var(--amber)', borderColor: 'rgba(176,122,18,0.4)' }}
                  title="今日できなかった予定を次の確定シフト日に移します"
                >
                  延期
                </button>
              )}

              {/* 日付移動 */}
              {!isDone && (
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => { setEditDate(!editDate); setDateVal(item.plan_date) }}
                  disabled={acting}
                  style={{ color: 'var(--blue)' }}
                  title="任意の日に手動で移動します"
                >
                  日付移動
                </button>
              )}

              {/* 固定 */}
              <button
                className={`btn btn-sm ${item.is_locked ? 'btn-primary' : 'btn-outline'}`}
                onClick={handleLock}
                disabled={acting}
                style={{ color: item.is_locked ? undefined : 'var(--blue)' }}
                title={item.is_locked ? '固定解除（再生成で削除対象になります）' : '固定すると再生成しても削除されません'}
              >
                {item.is_locked ? '🔒解除' : '固定'}
              </button>

              {/* スキップ */}
              {!isDone && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleSkip}
                  disabled={acting}
                  style={{ color: 'var(--ink-3)' }}
                  title="この予定をスキップ（やらない）扱いにします"
                >
                  スキップ
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// メインページ
// ──────────────────────────────────────────────────────────

export default function ProductionPlan() {
  const [startDate, setStartDate]   = useState(getTodayJST)
  const [numDays, setNumDays]       = useState<7 | 14 | 31>(7)

  // デフォルトは「保存済み計画」、選択状態を localStorage に永続化
  const PLAN_TAB_KEY = 'mangaos_production_plan_tab'
  const [activeTab, setActiveTabState] = useState<'preview' | 'saved'>(() => {
    try {
      const saved = localStorage.getItem(PLAN_TAB_KEY)
      if (saved === 'preview' || saved === 'saved') return saved
    } catch {}
    return 'saved'
  })
  const setActiveTab = (t: 'preview' | 'saved') => {
    try { localStorage.setItem(PLAN_TAB_KEY, t) } catch {}
    setActiveTabState(t)
  }

  const [previewDays, setPreviewDays]                   = useState<ProductionPlanDay[] | null>(null)
  const [previewDaySummaries, setPreviewDaySummaries]   = useState<ProductionPlanDaySummary[]>([])
  const [savedItems, setSavedItems]                     = useState<ProductionPlanItem[]>([])
  const [daySummaries, setDaySummaries]                 = useState<ProductionPlanDaySummary[]>([])

  const [previewLoading, setPreviewLoading] = useState(false)
  const [savedLoading,   setSavedLoading]   = useState(false)
  const [saving,         setSaving]         = useState(false)
  const [toast, setToast]                   = useState<{ msg: string; ok: boolean } | null>(null)

  // 圧縮リスト用
  const [selectedItemId, setSelectedItemId]           = useState<string | null>(null)
  const [showSkipped, setShowSkipped]                 = useState(false)
  const [filterWorker, setFilterWorker]               = useState<'all' | 'artist' | 'assistant'>('all')
  const [filterRisk,   setFilterRisk]                 = useState<'all' | 'danger' | 'pending'>('all')

  // Phase 2: 保存済みタブ内のビュー切替
  const PLAN_VIEW_KEY = 'mangaos_production_plan_view'
  const [planView, setPlanViewState] = useState<'compact' | 'weekly' | 'load'>(() => {
    try {
      const v = localStorage.getItem(PLAN_VIEW_KEY)
      if (v === 'compact' || v === 'weekly' || v === 'load') return v
    } catch {}
    return 'compact'
  })
  const setPlanView = (v: 'compact' | 'weekly' | 'load') => {
    try { localStorage.setItem(PLAN_VIEW_KEY, v) } catch {}
    setPlanViewState(v)
  }

  const endDate = addDays(startDate, numDays - 1)

  const showToast = (ok: boolean, msg: string) => {
    setToast({ ok, msg })
    setTimeout(() => setToast(null), 4000)
  }

  const loadSaved = async () => {
    setSavedLoading(true)
    try {
      const res = await api.productionPlanning.listPlans({ start_date: startDate, end_date: endDate })
      setSavedItems(res.items)
      setDaySummaries(res.day_summaries ?? [])
    } catch {
      // テーブル未作成時はエラーを無視
    } finally {
      setSavedLoading(false)
    }
  }

  useEffect(() => { loadSaved() }, [startDate, numDays]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreview = async () => {
    setPreviewLoading(true)
    try {
      const res = await api.productionPlanning.planPreview({ start_date: startDate, days: numDays })
      setPreviewDays(res.days)
      setPreviewDaySummaries(res.day_summaries ?? [])
      setActiveTab('preview')
    } catch (err) {
      showToast(false, (err as Error).message ?? 'プレビュー生成に失敗しました')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await api.productionPlanning.generatePlan({
        start_date: startDate,
        days: numDays,
        overwrite_unlocked: true,
        note: '自動生成',
      })
      showToast(true, `${res.created_count}件の予定を保存しました（固定 ${res.kept_locked_count} 件を保持）`)
      await loadSaved()
      setActiveTab('saved')
    } catch (err) {
      showToast(false, (err as Error).message ?? '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleItemUpdated = (updated: ProductionPlanItem) => {
    setSavedItems((prev) => prev.map((it) => it.id === updated.id ? updated : it))
  }

  // ── データ導出 ──────────────────────────────────────────

  // フィルタ適用（保存済みタブ用）
  const visibleSaved = savedItems.filter((it) => {
    if (!showSkipped && it.status === 'skipped') return false
    if (filterWorker !== 'all' && (it.worker_type ?? 'artist') !== filterWorker) return false
    if (filterRisk === 'danger' && !['critical', 'danger', 'warning'].includes(it.risk ?? '')) return false
    if (filterRisk === 'pending' && it.status === 'done') return false
    return true
  })

  const savedByDate = visibleSaved.reduce<Record<string, ProductionPlanItem[]>>((acc, it) => {
    if (!acc[it.plan_date]) acc[it.plan_date] = []
    acc[it.plan_date].push(it)
    return acc
  }, {})
  const savedDates = Object.keys(savedByDate).sort()

  const summaryByDate = new Map<string, ProductionPlanDaySummary>()
  for (const s of daySummaries) summaryByDate.set(s.date, s)

  const previewSummaryByDate = new Map<string, ProductionPlanDaySummary>()
  for (const s of previewDaySummaries) previewSummaryByDate.set(s.date, s)

  const previewWeekdaySummaries = previewDaySummaries.filter((s) => isWeekday(s.date))
  const previewHasNoAssistantShifts =
    previewWeekdaySummaries.length > 0 &&
    previewWeekdaySummaries.every((s) => (s.assistant_capacity ?? 0) === 0)

  const savedWeekdaySummaries = daySummaries.filter((s) => isWeekday(s.date))
  const savedHasNoAssistantShifts =
    savedWeekdaySummaries.length > 0 &&
    savedWeekdaySummaries.every((s) => (s.assistant_capacity ?? 0) === 0)

  const previewTotal = previewDays ? previewDays.reduce((s, d) => s + d.items.length, 0) : 0

  // 選択中アイテム（savedItems から導出 — フィルタに関係なく全体から探す）
  const selectedItem = selectedItemId
    ? (savedItems.find((it) => it.id === selectedItemId) ?? null)
    : null

  // ── JSX ────────────────────────────────────────────────

  return (
    <div>
      {/* パンくず・ヘッダー */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Link to="/works" style={{ textDecoration: 'none' }}>
          <button className="btn btn-outline btn-sm" style={{ fontSize: 12 }}>← 作品・話数</button>
        </Link>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Link to="/" style={{ color: 'var(--ink-3)', textDecoration: 'none' }}>ホーム</Link>
          <span>›</span>
          <Link to="/works" style={{ color: 'var(--ink-3)', textDecoration: 'none' }}>作品・話数</Link>
          <span>›</span>
          <span>制作計画</span>
        </div>
      </div>

      {/* 操作パネル */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">制作計画</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>開始日</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ fontSize: 13 }}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>日数</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {([
                  { days: 7,  label: '7日'   },
                  { days: 14, label: '14日'  },
                  { days: 31, label: '1か月' },
                ] as const).map(({ days, label }) => (
                  <button
                    key={days}
                    className={`btn btn-sm ${numDays === days ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setNumDays(days)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={handlePreview}
                disabled={previewLoading || saving}
              >
                {previewLoading ? '生成中...' : 'プレビュー生成'}
              </button>
              {previewDays && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                  disabled={saving || previewLoading}
                  style={{ background: 'var(--green)', borderColor: 'var(--green)' }}
                >
                  {saving ? '保存中...' : `この計画を保存 (${previewTotal}件)`}
                </button>
              )}
              <button
                className="btn btn-outline btn-sm"
                onClick={loadSaved}
                disabled={savedLoading}
              >
                {savedLoading ? '読込中...' : '保存済みを再読み込み'}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
            期間: {startDate} 〜 {endDate}
          </div>
        </div>
      </div>

      {/* タブ切り替え */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        <button
          className={`btn ${activeTab === 'preview' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('preview')}
        >
          プレビュー {previewDays ? `(${previewTotal}件)` : ''}
        </button>
        <button
          className={`btn ${activeTab === 'saved' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('saved')}
        >
          保存済み計画 ({savedItems.filter((it) => it.status !== 'skipped').length}件)
        </button>
      </div>

      {/* ─── プレビュータブ ─── */}
      {activeTab === 'preview' && (
        <div>
          {!previewDays && !previewLoading && (
            <div className="card">
              <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 13, padding: 24, textAlign: 'center' }}>
                「プレビュー生成」を押すと、{startDate} から {numDays} 日間の制作計画を表示します。
              </div>
            </div>
          )}
          {previewLoading && (
            <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 12 }}>計算中...</div>
          )}
          {previewDays && !previewLoading && previewHasNoAssistantShifts && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, borderRadius: 6,
              background: 'var(--amber-light)', border: '1px solid var(--amber)',
              fontSize: 12, color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontWeight: 700 }}>⚠</span>
              この期間にアシスタント確定シフトがありません。下書き・背景・仕上げなどのアシスタント作業は自動配置されません。
            </div>
          )}
          {previewDays && !previewLoading && previewDays.map((day) => {
            const pvSummary         = previewSummaryByDate.get(day.date)
            let artistUsed          = pvSummary?.artist_used ?? 0
            let assistantUsed       = pvSummary?.assistant_used ?? 0
            if (!pvSummary) {
              for (const it of day.items) {
                const wt = it.worker_type ?? 'artist'
                const cc = it.capacity_cost ?? 0
                if (wt === 'assistant') assistantUsed += cc
                else artistUsed += cc
              }
            }
            const assistantCapacity = pvSummary?.assistant_capacity ?? 0
            const assistantNames    = pvSummary?.assistant_names ?? []
            const assistantsByRole  = pvSummary?.assistants_by_role
            const roleConflictNotes = pvSummary?.role_conflict_notes ?? []
            const conflictLevel     = pvSummary?.conflict_level ?? null
            const conflictNote      = pvSummary?.conflict_note ?? null
            const assistantUsage    = pvSummary?.assistant_usage ?? []
            const capabilityNotes   = pvSummary?.capability_notes ?? []
            const hasConflict       = conflictLevel != null
            const isOverCapacity    = conflictLevel === 'over_capacity'
            const dayIsWeekday      = isWeekday(day.date)
            const showAssistantSection = dayIsWeekday && (artistUsed > 0 || day.items.length > 0 || assistantCapacity > 0)

            return (
              <div key={day.date} className="card" style={{
                marginBottom: 12,
                border: hasConflict
                  ? `1px solid ${isOverCapacity ? 'var(--red)' : 'var(--amber)'}`
                  : undefined,
              }}>
                <div className="card-header" style={{
                  background: hasConflict
                    ? (isOverCapacity ? 'var(--red-light)' : 'var(--amber-light)')
                    : 'var(--paper-2)',
                  flexDirection: 'column', alignItems: 'flex-start', gap: 6,
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{formatDateJP(day.date)}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        {day.items.length === 0 ? '作業なし' : `${day.items.length}件`}
                      </span>
                      {hasConflict && (
                        <span style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 3,
                          background: isOverCapacity ? 'var(--red)' : 'var(--amber)',
                          color: 'var(--paper)', fontWeight: 700,
                        }}>
                          {isOverCapacity ? '⚠ キャパ超過' : '⚠ キャパ注意'}
                        </span>
                      )}
                    </div>
                    {(artistUsed > 0 || showAssistantSection) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                        {artistUsed > 0 && (
                          <CapacityBar label="漫画家" used={artistUsed} conflict={conflictLevel} />
                        )}
                        {showAssistantSection && (
                          <CapacityBar
                            label="アシスタント"
                            used={assistantUsed}
                            capacity={assistantCapacity}
                            conflict={conflictLevel}
                          />
                        )}
                        {dayIsWeekday && assistantUsage.length === 0 && (
                          <div style={{ fontSize: 10, color: 'var(--ink-3)', paddingLeft: 78 }}>
                            {assistantNames.length > 0
                              ? `出勤: ${formatAssistantNames(assistantsByRole, assistantNames)}`
                              : '出勤なし'}
                          </div>
                        )}
                        {assistantUsage.length > 0 && (
                          <div style={{ paddingLeft: 78, marginTop: 2 }}>
                            {assistantUsage.map((au) => (
                              <div key={au.assistant_id} style={{ fontSize: 10, color: 'var(--ink-2)', display: 'flex', gap: 4 }}>
                                <span style={{ fontWeight: 600 }}>{au.name}</span>
                                <span style={{ fontFamily: 'var(--font-mono)', color: au.used_capacity > 0.8 ? 'var(--amber)' : 'var(--ink-3)' }}>
                                  {au.used_capacity.toFixed(2)}/{(au.used_capacity + au.remaining_capacity).toFixed(1)}
                                </span>
                                {au.assigned_stages.length > 0 && (
                                  <span style={{ color: 'var(--ink-3)' }}>
                                    ({au.assigned_stages.map((s) => STAGE_LABEL_SHORT[s] ?? s).join('・')})
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {conflictNote && (
                          <div style={{ fontSize: 10, color: 'var(--red)', fontWeight: 600, paddingLeft: 78 }}>
                            ⚠ {conflictNote}
                          </div>
                        )}
                        {capabilityNotes.map((note, i) => (
                          <div key={i} style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 600, paddingLeft: 78 }}>
                            ⚠ {note}
                          </div>
                        ))}
                        {capabilityNotes.length === 0 && roleConflictNotes.map((note, i) => (
                          <div key={i} style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 600, paddingLeft: 78 }}>
                            ⚠ {note}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {day.items.length === 0 ? (
                  <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                    この日の作業はありません（土日またはすべて完了）
                  </div>
                ) : (
                  <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {day.items.map((item, idx) => (
                      <PreviewItemRow key={`${item.episode_id}-${item.stage_key}-${idx}`} item={item} />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ─── 保存済みタブ ─── */}
      {activeTab === 'saved' && (
        <div>
          {/* KPI カード */}
          {!savedLoading && savedItems.length > 0 && (
            <KpiCards items={savedItems} daySummaries={daySummaries} />
          )}

          {/* アラートパネル（危険/注意/未割当タスク） */}
          {!savedLoading && savedItems.length > 0 && (
            <AlertPanel
              items={savedItems}
              onSelect={(id) => {
                setSelectedItemId(id)
                if (planView === 'load') setPlanView('compact')
              }}
            />
          )}

          {/* ビュー切替タブ */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
            {([
              { key: 'compact', label: '圧縮リスト' },
              { key: 'weekly',  label: '週次ボード' },
              { key: 'load',    label: '担当者負荷' },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                className={`btn btn-sm ${planView === key ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPlanView(key)}
                style={{ fontSize: 11 }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* アシスタントシフトなし警告 */}
          {!savedLoading && savedItems.length > 0 && savedHasNoAssistantShifts && (
            <div style={{
              padding: '10px 14px', marginBottom: 10, borderRadius: 6,
              background: 'var(--amber-light)', border: '1px solid var(--amber)',
              fontSize: 12, color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontWeight: 700 }}>⚠</span>
              この期間にアシスタント確定シフトがありません。下書き・背景・仕上げなどのアシスタント作業は自動配置されません。
            </div>
          )}

          {savedLoading && (
            <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 12 }}>読込中...</div>
          )}

          {!savedLoading && savedItems.length === 0 && (
            <div className="card">
              <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 13, padding: 24, textAlign: 'center' }}>
                {startDate} 〜 {endDate} の保存済み計画はありません。
                「プレビュー生成」→「この計画を保存」で計画を作成できます。
              </div>
            </div>
          )}

          {!savedLoading && savedItems.length > 0 && (
            <>
              {/* ── 圧縮リストビュー ── */}
              {planView === 'compact' && (
                <>
                  {/* フィルタバー */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {(['all', 'artist', 'assistant'] as const).map((w) => (
                        <button
                          key={w}
                          className={`btn btn-sm ${filterWorker === w ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => setFilterWorker(w)}
                          style={{ fontSize: 11, padding: '3px 8px' }}
                        >
                          {w === 'all' ? '全員' : w === 'artist' ? '漫画家' : 'アシスタント'}
                        </button>
                      ))}
                    </div>
                    <span style={{ color: 'var(--paper-3)', fontSize: 16, userSelect: 'none' }}>|</span>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {([
                        { key: 'all',     label: '全件' },
                        { key: 'danger',  label: '危険/注意' },
                        { key: 'pending', label: '未完了のみ' },
                      ] as const).map(({ key, label }) => (
                        <button
                          key={key}
                          className={`btn btn-sm ${filterRisk === key ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => setFilterRisk(key)}
                          style={{ fontSize: 11, padding: '3px 8px' }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <span style={{ color: 'var(--paper-3)', fontSize: 16, userSelect: 'none' }}>|</span>
                    <label style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={showSkipped}
                        onChange={(e) => setShowSkipped(e.target.checked)}
                      />
                      スキップ済みを含む
                    </label>
                    {visibleSaved.length !== savedItems.length && (
                      <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        ({visibleSaved.length}/{savedItems.length}件表示)
                      </span>
                    )}
                  </div>

                  {savedDates.length === 0 && (
                    <div style={{ color: 'var(--ink-3)', fontSize: 12, padding: '12px 0' }}>
                      フィルタ条件に一致する計画がありません
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* 左: 日別圧縮リスト */}
                    <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {savedDates.map((date) => {
                        const dayItems      = savedByDate[date]
                        const doneCount     = dayItems.filter((it) => it.status === 'done').length
                        const summary       = summaryByDate.get(date)
                        const conflictLevel = summary?.conflict_level ?? null
                        const hasConflict   = conflictLevel != null
                        const isOverCap     = conflictLevel === 'over_capacity'
                        const isWeekdayDate = isWeekday(date)

                        const artistUsed    = summary?.artist_used
                          ?? dayItems.filter((it) => (it.worker_type ?? 'artist') === 'artist').reduce((s, it) => s + (it.capacity_cost ?? 0), 0)
                        const assistantUsed = summary?.assistant_used
                          ?? dayItems.filter((it) => it.worker_type === 'assistant').reduce((s, it) => s + (it.capacity_cost ?? 0), 0)
                        const assistantCap  = summary?.assistant_capacity ?? 0
                        const assistantUsage    = summary?.assistant_usage ?? []
                        const capabilityNotes   = summary?.capability_notes ?? []
                        const roleConflictNotes = summary?.role_conflict_notes ?? []
                        const conflictNote      = summary?.conflict_note ?? null

                        return (
                          <div key={date} className="card" style={{
                            overflow: 'hidden',
                            border: hasConflict ? `1px solid ${isOverCap ? 'var(--red)' : 'var(--amber)'}` : undefined,
                          }}>
                            <div style={{
                              padding: '8px 12px',
                              background: hasConflict ? (isOverCap ? 'var(--red-light)' : 'var(--amber-light)') : 'var(--paper-2)',
                              borderBottom: '1px solid var(--paper-3)',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: 13 }}>{formatDateJP(date)}</span>
                                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{doneCount}/{dayItems.length}完了</span>
                                {hasConflict && (
                                  <span style={{
                                    fontSize: 9, padding: '1px 5px', borderRadius: 4,
                                    background: isOverCap ? 'var(--red)' : 'var(--amber)',
                                    color: 'var(--paper)', fontWeight: 700,
                                  }}>
                                    {isOverCap ? '⚠超過' : '⚠注意'}
                                  </span>
                                )}
                              </div>
                              {(artistUsed > 0 || (isWeekdayDate && assistantCap > 0)) && (
                                <div style={{ display: 'flex', gap: 14, marginTop: 4, flexWrap: 'wrap' }}>
                                  {artistUsed > 0 && <MiniCapBar label="漫画家" used={artistUsed} capacity={1.0} />}
                                  {isWeekdayDate && <MiniCapBar label="アシ" used={assistantUsed} capacity={assistantCap} />}
                                </div>
                              )}
                              {assistantUsage.length > 0 && (
                                <div style={{ marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {assistantUsage.map((au) => (
                                    <span key={au.assistant_id} style={{ fontSize: 9, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                                      {au.name} {au.used_capacity.toFixed(1)}/{(au.used_capacity + au.remaining_capacity).toFixed(0)}
                                      {au.assigned_stages.length > 0 ? `(${au.assigned_stages.map((st) => STAGE_LABEL_SHORT[st] ?? st).join('・')})` : ''}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {conflictNote && (
                                <div style={{ fontSize: 9, color: 'var(--red)', fontWeight: 600, marginTop: 2 }}>⚠ {conflictNote}</div>
                              )}
                              {capabilityNotes.map((n, i) => (
                                <div key={i} style={{ fontSize: 9, color: 'var(--amber)', fontWeight: 600, marginTop: 2 }}>⚠ {n}</div>
                              ))}
                              {capabilityNotes.length === 0 && roleConflictNotes.map((n, i) => (
                                <div key={i} style={{ fontSize: 9, color: 'var(--amber)', fontWeight: 600, marginTop: 2 }}>⚠ {n}</div>
                              ))}
                            </div>
                            <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {dayItems.map((item) => (
                                <CompactTaskCard
                                  key={item.id}
                                  item={item}
                                  isSelected={item.id === selectedItemId}
                                  onClick={() => setSelectedItemId((prev) => prev === item.id ? null : item.id)}
                                />
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    {/* 右: 詳細パネル */}
                    <div style={{
                      width: 320, minWidth: 260, flexShrink: 0,
                      position: 'sticky', top: 68, alignSelf: 'flex-start',
                      maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
                    }}>
                      {selectedItem ? (
                        <ItemDetailPanel
                          key={selectedItem.id}
                          item={selectedItem}
                          daySummary={summaryByDate.get(selectedItem.plan_date) ?? null}
                          onUpdated={(updated) => {
                            handleItemUpdated(updated)
                            if (updated.plan_date !== selectedItem.plan_date) loadSaved()
                          }}
                          showToast={showToast}
                          onClose={() => setSelectedItemId(null)}
                        />
                      ) : (
                        <div className="card">
                          <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 12, textAlign: 'center', padding: '32px 16px', lineHeight: 1.7 }}>
                            <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.4 }}>☰</div>
                            左のタスクをクリックすると<br />詳細と操作ボタンが表示されます
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ── 週次ボードビュー ── */}
              {planView === 'weekly' && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 300 }}>
                    <WeeklyBoardView
                      items={savedItems}
                      daySummaries={daySummaries}
                      startDate={startDate}
                      selectedItemId={selectedItemId}
                      onSelect={setSelectedItemId}
                    />
                  </div>
                  {selectedItem && (
                    <div style={{
                      width: 320, minWidth: 260, flexShrink: 0,
                      position: 'sticky', top: 68, alignSelf: 'flex-start',
                      maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
                    }}>
                      <ItemDetailPanel
                        key={selectedItem.id}
                        item={selectedItem}
                        daySummary={summaryByDate.get(selectedItem.plan_date) ?? null}
                        onUpdated={(updated) => {
                          handleItemUpdated(updated)
                          if (updated.plan_date !== selectedItem.plan_date) loadSaved()
                        }}
                        showToast={showToast}
                        onClose={() => setSelectedItemId(null)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ── 担当者負荷ビュー ── */}
              {planView === 'load' && (
                <LoadMatrixView
                  items={savedItems}
                  onSelectTask={(id) => {
                    setSelectedItemId(id)
                    setPlanView('compact')
                  }}
                  onViewChange={setPlanView}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* トースト */}
      {toast && (
        <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />
      )}
    </div>
  )
}
